/**
 * Klaverjassen explained.
 *
 * The card values are a table with nothing underneath them, so they are shown
 * as a table. Everything else on this page exists to answer the two questions a
 * new player actually has: why did the jack win, and what does "nat" mean.
 */

import type { ReactNode } from 'react';
import { Panel, Screen } from '@/components/ui';

const TRUMP = [
  ['B', 20],
  ['9', 14],
  ['A', 11],
  ['10', 10],
  ['H', 4],
  ['V', 3],
  ['8', 0],
  ['7', 0],
] as const;

const PLAIN = [
  ['A', 11],
  ['10', 10],
  ['H', 4],
  ['V', 3],
  ['B', 2],
  ['9', 0],
  ['8', 0],
  ['7', 0],
] as const;

export default function KlaverjasRules() {
  return (
    <Screen title="Klaverjassen" back="/regels" subtitle="Twee tegen twee · 32 kaarten">
      <Panel title="Waar het om gaat">
        <Prose>
          <p>
            Je speelt met z'n vieren, twee tegen twee, met je maat tegenover je. Er wordt gedeeld
            uit een pak van <strong>32 kaarten</strong> — zeven tot en met aas — en iedereen krijgt
            er acht.
          </p>
          <p>
            Eén partij kiest de troef en heeft daarmee iets te bewijzen: die partij moet{' '}
            <strong>meer dan de helft</strong> halen. Lukt dat niet, dan gaat álles naar de
            tegenstander.
          </p>
        </Prose>
      </Panel>

      <Panel title="1 · Wat de kaarten waard zijn">
        <Prose>
          <Callout>
            In troef slaat de <strong>boer</strong> alles, en de negen komt daar meteen achteraan.
            Buiten troef doen die twee bijna niets. Dat is het eerste wat verwarrend is en het enige
            wat je echt uit je hoofd moet leren.
          </Callout>

          <div className="grid grid-cols-2 gap-4">
            <Table title="In troef" rows={TRUMP} />
            <Table title="Buiten troef" rows={PLAIN} />
          </div>

          <p>
            Samen zit er 152 in de kaarten. De laatste slag is nog eens 10 waard, dus er gaat elke
            gift <strong>162</strong> over tafel.
          </p>
        </Prose>
      </Panel>

      <Panel title="2 · Roem">
        <Prose>
          <p>Extra punten voor kaarten die in dezelfde slag vallen:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Drie op een rij</strong> in dezelfde kleur: 20 punten.
            </li>
            <li>
              <strong>Vier op een rij</strong>: 50 punten.
            </li>
            <li>
              <strong>Heer en vrouw van troef</strong> samen ("stuk"): 20 punten.
            </li>
          </ul>
          <p>
            Rijtjes tellen op de gewone volgorde 7-8-9-10-B-V-H-A — dus boer, vrouw, heer ís een
            rijtje, ook al slaat de boer de heer in troef. Roem gaat naar de partij die de slag
            pakt, en stapelt: boer, vrouw en heer van troef is 20 voor het rijtje plus 20 voor het
            stuk.
          </p>
        </Prose>
      </Panel>

      <Panel title="3 · Nat gaan">
        <Prose>
          <Callout>
            De partij die troef koos moet méér halen dan de tegenpartij, roem meegeteld. Gelijk is
            niet genoeg. Halen ze het niet, dan zijn ze <strong>nat</strong>: de tegenstander krijgt
            alle 162 plus alle roem, en zijzelf nul.
          </Callout>
          <p>
            Daarom is troef kiezen een gok. Met een matige hand pas je liever — als de regels dat
            toelaten.
          </p>
        </Prose>
      </Panel>

      <Panel title="4 · Rotterdams of Amsterdams">
        <Prose>
          <p>Beide varianten zijn het over dit eens:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Kleur bekennen als je kunt.</li>
            <li>Wordt troef uitgekomen, dan moet je troef volgen en overtroeven als je kunt.</li>
          </ul>
          <p>Het verschil zit in wat er gebeurt als je niet kunt bekennen:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Rotterdams</strong>: je moet altijd troeven, ook als je maat de slag al heeft.
              Kun je niet overtroeven, dan leg je er alsnog een lagere troef onder.
            </li>
            <li>
              <strong>Amsterdams</strong>: heeft je maat de slag, dan hoef je niet te troeven. En
              kun je niet overtroeven, dan mag je afgooien in plaats van een troef weg te geven.
            </li>
          </ul>
          <p className="text-slate-400">
            Rotterdams is harder: de troeven zijn er veel sneller uit. De app zet kaarten die je
            niet mag spelen grijs; tik er gerust op, hij vertelt waarom niet.
          </p>
        </Prose>
      </Panel>

      <Panel title="5 · Pit">
        <Prose>
          <p>
            Pakt één partij <strong>alle acht de slagen</strong>, dan is dat pit: 100 punten extra.
            Uit te zetten bij het opzetten van het potje.
          </p>
        </Prose>
      </Panel>

      <Panel title="Veelgemaakte fouten">
        <Prose>
          <ul className="list-disc space-y-1 pl-5">
            <li>De tien onderschatten. Die is 10 punten en slaat de heer, overal.</li>
            <li>
              Troef kiezen op een hand met veel plaatjes maar weinig troef. Je hebt de slagen nodig,
              niet de punten.
            </li>
            <li>Vergeten dat gelijk spelen ook nat is. Je moet er écht overheen.</li>
          </ul>
        </Prose>
      </Panel>
    </Screen>
  );
}

function Table({ title, rows }: { title: string; rows: readonly (readonly [string, number])[] }) {
  return (
    <div>
      <p className="mb-1 text-xs uppercase tracking-wide text-slate-500">{title}</p>
      <table className="w-full text-sm tabular-nums">
        <tbody>
          {rows.map(([rank, points]) => (
            <tr key={rank} className="border-t border-white/5">
              <td className="py-0.5 font-medium">{rank}</td>
              <td className={`py-0.5 text-right ${points === 0 ? 'text-slate-600' : ''}`}>
                {points}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Prose({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-relaxed text-slate-300">{children}</div>;
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border-l-2 border-accent-500 bg-accent-500/10 px-3 py-2 text-slate-200">
      {children}
    </p>
  );
}
